import React, { useMemo } from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Token } from 'vvs-sdk'
import { Text, Button, Input, InputProps, Flex, Link, Slider, useMatchBreakpoints } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import { parseUnits } from 'ethers/lib/utils'
import { formatBigNumber } from 'utils/formatBalance'
import useDebouncedChangeHandler from 'hooks/useDebouncedChangeHandler'
import { TokenPairImage } from 'components/TokenImage'
import LocalBalance from 'components/LocalBalance'
import { DEFAULT_TOKEN_DECIMAL_COUNT } from 'config'

interface FarmLpAmountInputProps {
  max: string
  symbol: string
  onSelectMax?: () => void
  onChange: (newValue: string) => void
  // eslint-disable-next-line react/no-unused-prop-types
  placeholder?: string
  value: string
  addLiquidityUrl?: string
  inputTitle?: string
  decimals?: number
  token: Token
  quoteToken: Token
  isWarning?: boolean
  valueInUsdHint?: BigNumber
}

const getBoxShadow = ({ isWarning = false, theme }) => {
  if (isWarning) {
    return theme.shadows.warning
  }

  return theme.shadows.inset
}

const StyledTokenInput = styled.div<InputProps>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 16px;
  box-shadow: ${getBoxShadow};
  color: ${({ theme }) => theme.colors.text};
  padding: 8px 16px 8px 0;
  width: 100%;
`

const StyledInput = styled(Input)`
  box-shadow: none;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  width: 60px;
  margin: 0 8px;
  padding: 0 8px;
  border: none;

  ${({ theme }) => theme.mediaQueries.xs} {
    width: 80px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    width: auto;
  }
`

const StyledErrorMessage = styled(Text)`
  position: absolute;
  bottom: -22px;
  a {
    display: inline;
  }
`

const TokenWrapper = styled.div`
  top: 4px;
  position: relative;
  padding-right: 8px;
  width: 50px;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 45px;
  }
`

const FarmLpAmountInput: React.FC<FarmLpAmountInputProps> = ({
  max,
  symbol,
  onChange,
  onSelectMax,
  value,
  addLiquidityUrl,
  inputTitle,
  decimals = 18,
  token,
  quoteToken,
  isWarning = false,
  valueInUsdHint,
}) => {
  const { t } = useTranslation()
  const isBalanceZero = max === '0' || !max
  const { isMobile } = useMatchBreakpoints()

  const displayBalance = (balance: string) => {
    if (isBalanceZero) {
      return '0'
    }

    const balanceUnits = parseUnits(balance, decimals)
    return formatBigNumber(balanceUnits, decimals, decimals)
  }

  const [percentage, setPercentage] = useDebouncedChangeHandler(
    useMemo(() => {
      const percentageValue = new BigNumber(value).times(100).div(max)
      if (percentageValue.isNaN()) return 0
      return Math.max(Math.min(percentageValue.toNumber(), 100), 0)
    }, [value, max]),
    (newPercentage: number) => {
      if (newPercentage >= 100) {
        onChange(max)
      } else {
        onChange(new BigNumber(max).div(100).times(newPercentage).toFixed(DEFAULT_TOKEN_DECIMAL_COUNT))
      }
    },
  )

  return (
    <div style={{ position: 'relative' }}>
      <StyledTokenInput isWarning={isBalanceZero || isWarning}>
        <Flex justifyContent="space-between" pl="16px">
          <Text fontSize="14px">{inputTitle}</Text>
          <Text fontSize="14px">{t('Balance: %balance%', { balance: displayBalance(max) })}</Text>
        </Flex>
        <Flex alignItems="center" justifyContent="space-around">
          <StyledInput
            pattern={`^[0-9]*[.,]?[0-9]{0,${decimals}}$`}
            inputMode="decimal"
            step="any"
            min="0"
            onChange={(e) => {
              if (e.currentTarget.validity.valid) {
                onChange(e.currentTarget.value.replace(/,/g, '.'))
              }
            }}
            placeholder="0"
            value={value}
          />
          {!isMobile ? (
            <TokenWrapper>
              <TokenPairImage
                variant="inverted"
                primaryToken={token}
                secondaryToken={quoteToken}
                width={28}
                height={28}
              />
            </TokenWrapper>
          ) : null}
          <Text fontSize="16px">{symbol}</Text>
        </Flex>
        <LocalBalance
          pl={3}
          prefix="~ "
          color="textSubtle"
          fontSize="13px"
          value={valueInUsdHint && !valueInUsdHint.isNaN() ? valueInUsdHint : new BigNumber(0)}
        />
        <Flex alignItems="center">
          <Slider
            flex="1"
            mt="20px"
            mb="8px"
            name="lp-amount"
            min={0}
            max={100}
            value={percentage}
            onValueChanged={setPercentage}
          />
          <Text fontSize="20px" fontWeight="600" textAlign="right" width="70px">
            {percentage.toFixed(1)}%
          </Text>
        </Flex>
        <Flex justifyContent="center" mt={1} mb={2}>
          <Button variant="capsule" onClick={() => setPercentage(25)}>
            25%
          </Button>
          <Button variant="capsule" onClick={() => setPercentage(50)}>
            50%
          </Button>
          <Button variant="capsule" onClick={() => setPercentage(75)}>
            75%
          </Button>
          <Button width="auto" variant="capsule" onClick={onSelectMax}>
            {t('Max')}
          </Button>
        </Flex>
      </StyledTokenInput>
      {isBalanceZero && (
        <StyledErrorMessage fontSize="14px" color="failure">
          {t('No tokens to stake')}:{' '}
          <Link fontSize="14px" bold={false} href={addLiquidityUrl} external color="failure">
            {t('Create %lpSymbol%', { lpSymbol: symbol })}
          </Link>
        </StyledErrorMessage>
      )}
    </div>
  )
}

export default FarmLpAmountInput
